import { useCallback, useEffect, useRef, useState } from "react";
import { resolvePath, useFetcher, useSearchParams } from "react-router-dom";
import { objectToSearchParams, removeEmpty, searchParamsToObject } from '../library/helpers';

const getSearchParamsKey = (paramsObject = {}) => {
  const queryParams = { ...paramsObject }
  delete queryParams.page
  delete queryParams.index

  const params = new URLSearchParams(objectToSearchParams(queryParams))

  params.sort()
  return params.toString()
}

function useFetch({ initialData }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher()
  const [hasMore, setHasMore] = useState(true)
  const searchParamsParsed = searchParamsToObject(searchParams)
  const searchParamsKey = getSearchParamsKey(searchParamsParsed)
  const [list, setList] = useState(initialData?.data || [])
  const currentPage = useRef(1)
  const searchParamsRef = useRef(searchParamsParsed) //Use
  const activeSearchParamsKeyRef = useRef(searchParamsKey)
  const loadMoreSearchParamsKeyRef = useRef(null)
  const loadMorePageRef = useRef(null)
  const initialLocation = useRef(resolvePath('.')); // Track component mount status

  useEffect(() => {
    initialLocation.current = resolvePath('.', location.pathname)?.pathname;
    return () => {
      initialLocation.current = null
      currentPage.current = 1;
    };
  }, []);

  useEffect(() => {
    setHasMore(false)
    searchParamsRef.current = searchParamsParsed
    activeSearchParamsKeyRef.current = searchParamsKey
    loadMoreSearchParamsKeyRef.current = null
    loadMorePageRef.current = null
    currentPage.current = 1
  }, [searchParamsKey])

  useEffect(() => {
    setList(initialData?.data || [])
    setHasMore(Boolean(initialData?.meta && (initialData.meta.totalPages > parseInt(initialData.meta.page))))
    searchParamsRef.current = searchParamsParsed
    activeSearchParamsKeyRef.current = searchParamsKey
    loadMoreSearchParamsKeyRef.current = null
    loadMorePageRef.current = null
    currentPage.current = parseInt(initialData?.meta?.page) || 1
  }, [initialData, searchParamsKey])

  useEffect(() => {
    if (!fetcher.data || fetcher.state === "loading") {
      return;
    }

    if (loadMoreSearchParamsKeyRef.current !== activeSearchParamsKeyRef.current) {
      return
    }

    if (Array.isArray(fetcher.data?.data)) {
      updateList(fetcher.data.data)
      currentPage.current = parseInt(fetcher?.data?.meta?.page) || loadMorePageRef.current || 1
      loadMoreSearchParamsKeyRef.current = null
      loadMorePageRef.current = null
      setHasMore(Boolean(fetcher.data?.meta && (fetcher.data.meta.totalPages > parseInt(fetcher.data.meta.page))))
    }
  }, [fetcher.data, fetcher.state])

  const setFilters = useCallback((f) => {
    setSearchParams((params) => {
      const currentParams = searchParamsToObject(params)
      const nextFilters = typeof f === 'function' ? f(currentParams.filter) : f
      const nextParams = { ...currentParams }
      delete nextParams.page
      delete nextParams.index

      return objectToSearchParams({
        ...nextParams,
        filter: removeEmpty(nextFilters),
      })
    })
  }, [setSearchParams])

  const setSort = useCallback((s) => {
    setSearchParams((params) => {
      const currentParams = searchParamsToObject(params)
      const nextParams = { ...currentParams }
      delete nextParams.page
      delete nextParams.index

      return objectToSearchParams({
        ...nextParams,
        sort: removeEmpty(s),
      })
    })
  }, [setSearchParams])

  const loadMore = useCallback(() => {
    if (fetcher.state === 'loading' || !hasMore || !initialLocation.current) return;

    const nextPage = currentPage.current + 1
    const params = new URLSearchParams(objectToSearchParams(searchParamsRef.current))
    params.set('page', nextPage)
    params.set('index', '')
    loadMoreSearchParamsKeyRef.current = activeSearchParamsKeyRef.current
    loadMorePageRef.current = nextPage
    fetcher.load(`${initialLocation.current}?` + params.toString())
  }, [fetcher, hasMore]);

  const updateList = (items) => {
    if (!items || !Array.isArray(items)) return;
    setList((prev) => [...(prev || []), ...items])
  }

  return [list, hasMore, fetcher.state === 'loading', searchParamsParsed, {
    setSort,
    setFilters,
    loadMore
  }];
}

export default useFetch;
